import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import VoiceCommandPanel from "@/components/VoiceCommandPanel";
import MedicationManager from "@/components/MedicationManager";
import AICareAssistant from "@/components/AICareAssistant";
// Import reusable components
import { QuickActionCard, UpcomingVisitsCard, NextVisitBanner, CareTeamCard, TeamMessagesCard, DailyCheckinDialog } from "@/components/family-hub";
import {
  Heart, Calendar, Clock, Phone, MessageSquare, Video, Bell,
  CheckCircle, AlertTriangle, Activity, Sun, Moon, Pill,
  FileText, Users, ChevronRight, Loader2, RefreshCw, Send,
  Sparkles, Brain, Shield, Star, BookOpen, Smile, Frown,
  ThermometerSun, Wind, Droplets, Utensils, BedDouble, Mic, Bot
} from "lucide-react";

export default function FamilyHub() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  
  // Dashboard data
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [careTeam, setCareTeam] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [lastCheckin, setLastCheckin] = useState(null);
  
  // Team Messages
  const [conversations, setConversations] = useState([]);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [newMessageSubject, setNewMessageSubject] = useState("");
  const [newMessageText, setNewMessageText] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  
  // Voice commands
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  
  // Check-in dialog
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [checkinData, setCheckinData] = useState({
    pain_level: 3,
    mood: "okay",
    symptoms: [],
    sleep_quality: "fair",
    appetite: "normal",
    notes: ""
  });
  const [isSubmittingCheckin, setIsSubmittingCheckin] = useState(false);
  
  // Caregiver wellness
  const [showWellnessDialog, setShowWellnessDialog] = useState(false);
  const [wellnessData, setWellnessData] = useState({
    stress_level: 5,
    sleep_hours: 7,
    had_respite: false,
    needs_support: false,
    notes: ""
  });
  
  // Medications section
  const [showMedicationsSection, setShowMedicationsSection] = useState(false);

  const isFamily = user?.role && ["patient", "family", "caregiver"].includes(user.role?.toLowerCase());

  useEffect(() => {
    if (authLoading) return;
    if (!isFamily) {
      navigate("/");
      return;
    }
    loadDashboardData();
  }, [user, authLoading]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [visitsRes, notificationsRes, teamRes, conversationsRes] = await Promise.all([
        api.get("/scheduling/my-visits?days_ahead=14").catch(() => ({ data: { visits: [] } })),
        api.get("/alerts/notifications?unread_only=false&limit=20").catch(() => ({ data: { notifications: [] } })),
        api.get("/assignments/my-care-team").catch(() => ({ data: { team: [] } })),
        api.get("/messages/conversations").catch(() => ({ data: { conversations: [] } }))
      ]);
      
      setUpcomingVisits(visitsRes.data.visits || []);
      setNotifications(notificationsRes.data.notifications || []);
      setCareTeam(teamRes.data.team || []);
      setConversations(conversationsRes.data.conversations || []);
      
      // Get patient info if family member
      if (user?.id) {
        const patientRes = await api.get(`/users/${user.id}`).catch(() => ({ data: null }));
        setPatientInfo(patientRes.data);
      }
    } catch (error) {
      console.error("Error loading family dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCheckin = async () => {
    setIsSubmittingCheckin(true);
    try {
      await api.post("/daily-checkins", {
        ...checkinData,
        user_id: user?.id
      });
      toast.success("Check-in submitted! Your care team has been notified.");
      setShowCheckinDialog(false);
      setLastCheckin(new Date().toISOString());
      
      // Show alert if high pain
      if (checkinData.pain_level >= 7) {
        toast.warning("We've alerted your care team about your pain level.", {
          duration: 5000
        });
      }
    } catch (error) {
      toast.error("Failed to submit check-in");
    } finally {
      setIsSubmittingCheckin(false);
    }
  };

  const handleSubmitWellness = async () => {
    try {
      await api.post("/caregiver/wellness", {
        ...wellnessData,
        caregiver_id: user?.id
      });
      toast.success("Wellness check submitted. We're here to support you.");
      setShowWellnessDialog(false);
      
      if (wellnessData.stress_level >= 8 || wellnessData.needs_support) {
        toast.info("A care coordinator will reach out to discuss support options.", {
          duration: 5000
        });
      }
    } catch (error) {
      toast.error("Failed to submit wellness check");
    }
  };

  // Team Messages handlers
  const handleSendNewMessage = async () => {
    if (!newMessageSubject.trim() || !newMessageText.trim()) {
      toast.error("Please enter a subject and message");
      return;
    }
    
    setIsSendingMessage(true);
    try {
      await api.post("/messages/conversations", {
        subject: newMessageSubject,
        message: newMessageText
      });
      toast.success("Message sent to your care team!");
      setShowMessageDialog(false);
      setNewMessageSubject("");
      setNewMessageText("");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleOpenConversation = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const res = await api.get(`/messages/conversations/${conversation.id}`);
      setConversationMessages(res.data.messages || []);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setIsSendingMessage(true);
    try {
      await api.post(`/messages/conversations/${selectedConversation.id}`, {
        message: replyText
      });
      setReplyText("");
      // Reload conversation
      const res = await api.get(`/messages/conversations/${selectedConversation.id}`);
      setConversationMessages(res.data.messages || []);
      toast.success("Reply sent!");
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const getMoodEmoji = (mood) => {
    switch (mood) {
      case "great": return "😊";
      case "good": return "🙂";
      case "okay": return "😐";
      case "sad": return "😢";
      case "anxious": return "😰";
      case "depressed": return "😔";
      default: return "😐";
    }
  };

  const getPainColor = (level) => {
    if (level <= 3) return "bg-green-500";
    if (level <= 5) return "bg-yellow-500";
    if (level <= 7) return "bg-orange-500";
    return "bg-red-500";
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center" data-testid="family-hub-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-600">{t("familyHub.loading")}</p>
        </div>
      </div>
    );
  }

  const nextVisit = upcomingVisits[0];
  const unreadNotifications = notifications.filter(n => !n.read).length;

  // Voice command handlers
  const handleVoicePainLevel = (level) => {
    setCheckinData(prev => ({ ...prev, pain_level: level }));
    toast.success(`Pain level set to ${level}`);
  };

  const handleVoiceMood = (mood) => {
    setCheckinData(prev => ({ ...prev, mood }));
    toast.success(`Mood set to ${mood}`);
  };

  const handleVoiceSymptom = (symptom) => {
    setCheckinData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms
        : [...prev.symptoms, symptom]
    }));
    toast.success(`Added symptom: ${symptom}`);
  };

  const handleVoiceStress = (level) => {
    setWellnessData(prev => ({ ...prev, stress_level: level }));
    toast.success(`Stress level set to ${level}`);
  };

  const handleVoiceSleep = (hours) => {
    setWellnessData(prev => ({ ...prev, sleep_hours: hours }));
    toast.success(`Sleep hours set to ${hours}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50" data-testid="family-hub">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                Welcome back, {user?.name?.split(' ')[0] || 'Friend'} 💙
              </h1>
              <p className="text-teal-100 mt-1">
                Your care hub • We&apos;re here for you
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowVoicePanel(true)}
                variant="outline"
                className="border-white/50 text-white hover:bg-white/10"
                data-testid="voice-mode-btn"
              >
                <Mic className="w-4 h-4 mr-2" />
                Voice Mode
              </Button>
              <Button
                onClick={() => setShowCheckinDialog(true)}
                className="bg-white text-teal-700 hover:bg-teal-50"
                data-testid="daily-checkin-btn"
              >
                <Heart className="w-4 h-4 mr-2" />
                Daily Check-in
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Command Panel */}
      <VoiceCommandPanel
        isOpen={showVoicePanel}
        onClose={() => setShowVoicePanel(false)}
        onPainLevel={handleVoicePainLevel}
        onMood={handleVoiceMood}
        onSymptom={handleVoiceSymptom}
        onCheckIn={() => setShowCheckinDialog(true)}
        onCallNurse={() => {
          toast.info("Connecting to your care team...");
          navigate("/telehealth");
        }}
        onNextVisit={() => {
          if (nextVisit) {
            toast.info(`Next visit: ${nextVisit.date} at ${nextVisit.start_time} with ${nextVisit.staff_name}`);
          } else {
            toast.info("No upcoming visits scheduled");
          }
        }}
        onTalkToGoodwin={() => navigate("/companion")}
        onVideoCall={() => navigate("/telehealth")}
        onStressLevel={handleVoiceStress}
        onSleepHours={handleVoiceSleep}
        onConfirm={handleSubmitCheckin}
        nextVisit={nextVisit}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions - Using Reusable QuickActionCard Components */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8" data-testid="quick-actions">
          <QuickActionCard
            icon={Heart}
            iconBgColor="bg-teal-100"
            iconColor="text-teal-600"
            title="Daily Check-in"
            subtitle="How are you feeling?"
            borderColor="border-l-teal-500"
            onClick={() => setShowCheckinDialog(true)}
          />

          <QuickActionCard
            icon={Bot}
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
            title="AI Assistant"
            subtitle="Ask any question"
            borderColor="border-l-purple-500"
            onClick={() => setShowAIAssistant(true)}
          />

          <QuickActionCard
            icon={Pill}
            iconBgColor="bg-amber-100"
            iconColor="text-amber-600"
            title="Medications"
            subtitle="Refills & reminders"
            borderColor="border-l-amber-500"
            onClick={() => setShowMedicationsSection(!showMedicationsSection)}
          />

          <QuickActionCard
            icon={Mic}
            iconBgColor="bg-indigo-100"
            iconColor="text-indigo-600"
            title="Voice Mode"
            subtitle="Hands-free control"
            borderColor="border-l-indigo-500"
            onClick={() => setShowVoicePanel(true)}
          />

          <QuickActionCard
            icon={Video}
            iconBgColor="bg-blue-100"
            iconColor="text-blue-600"
            title="Video Call"
            subtitle="Talk to care team"
            borderColor="border-l-blue-500"
            onClick={() => navigate("/telehealth")}
          />

          <QuickActionCard
            icon={Sparkles}
            iconBgColor="bg-rose-100"
            iconColor="text-rose-600"
            title="Self-Care"
            subtitle="Caregiver wellness"
            borderColor="border-l-rose-500"
            onClick={() => setShowWellnessDialog(true)}
          />
        </div>

        {/* Medications Section - Expandable */}
        {showMedicationsSection && (
          <div className="mb-8 animate-in slide-in-from-top duration-300" data-testid="medications-section">
            <MedicationManager />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Voice Mode Promo Banner */}
            <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg overflow-hidden" data-testid="voice-promo">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-white/20 rounded-full">
                      <Mic className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Hands-Free Mode</h3>
                      <p className="text-white/80 text-sm">
                        Say &quot;pain level 5&quot; or &quot;my mood is okay&quot; to check in without touching your device
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowVoicePanel(true)}
                    className="bg-white text-indigo-600 hover:bg-indigo-50"
                  >
                    Try Voice
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Next Visit Card */}
            {nextVisit && (
              <Card className="bg-white shadow-sm overflow-hidden" data-testid="next-visit-card">
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Your Next Visit
                  </h3>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-semibold text-slate-800">
                        {nextVisit.type_name || nextVisit.visit_type}
                      </p>
                      <p className="text-slate-600">
                        {nextVisit.formatted_date || nextVisit.date}
                      </p>
                      <p className="text-slate-500 text-sm">
                        {nextVisit.start_time} - {nextVisit.end_time}
                      </p>
                      <p className="text-teal-600 text-sm mt-1">
                        with {nextVisit.staff_name}
                      </p>
                    </div>
                    <div className="text-right">
                      {nextVisit.is_today && (
                        <Badge className="bg-green-100 text-green-700">Today!</Badge>
                      )}
                      {nextVisit.is_tomorrow && (
                        <Badge className="bg-blue-100 text-blue-700">Tomorrow</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Schedule - Using Reusable Component */}
            <UpcomingVisitsCard visits={upcomingVisits} maxVisible={5} />

            {/* Resources */}
            <Card className="bg-white shadow-sm" data-testid="resources-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                  Helpful Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="h-auto py-3 justify-start"
                    onClick={() => navigate("/videos")}
                  >
                    <Video className="w-5 h-5 mr-2 text-blue-500" />
                    <div className="text-left">
                      <p className="font-medium">Video Library</p>
                      <p className="text-xs text-slate-500">Educational content</p>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-3 justify-start"
                    onClick={() => navigate("/companion")}
                  >
                    <Brain className="w-5 h-5 mr-2 text-teal-500" />
                    <div className="text-left">
                      <p className="font-medium">AI Companion</p>
                      <p className="text-xs text-slate-500">Chat anytime</p>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-3 justify-start"
                  >
                    <FileText className="w-5 h-5 mr-2 text-orange-500" />
                    <div className="text-left">
                      <p className="font-medium">Care Guide</p>
                      <p className="text-xs text-slate-500">Tips & guidance</p>
                    </div>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-3 justify-start"
                  >
                    <Shield className="w-5 h-5 mr-2 text-green-500" />
                    <div className="text-left">
                      <p className="font-medium">My Records</p>
                      <p className="text-xs text-slate-500">Documents</p>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Notifications */}
            <Card className="bg-white shadow-sm" data-testid="notifications-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-orange-500" />
                    Notifications
                  </span>
                  {unreadNotifications > 0 && (
                    <Badge className="bg-orange-100 text-orange-700">{unreadNotifications}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {notifications.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No notifications</p>
                  ) : (
                    <div className="space-y-2">
                      {notifications.slice(0, 5).map((notif, index) => (
                        <div
                          key={notif.id || index}
                          className={`p-3 rounded-lg ${notif.read ? 'bg-slate-50' : 'bg-teal-50 border-l-2 border-l-teal-500'}`}
                        >
                          <p className="text-sm font-medium text-slate-800">{notif.title}</p>
                          <p className="text-xs text-slate-500 mt-1">{notif.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Care Team - Using extracted component */}
            <CareTeamCard 
              team={careTeam} 
              onCallMember={(member) => {
                if (member.phone) {
                  window.location.href = `tel:${member.phone}`;
                }
              }}
            />

            {/* Team Messages */}
            <Card className="bg-white shadow-sm" data-testid="team-messages-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-500" />
                    Messages
                  </span>
                  <Button 
                    size="sm" 
                    onClick={() => setShowMessageDialog(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="new-message-btn"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    New
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[180px]">
                  {conversations.length === 0 ? (
                    <div className="text-center py-4">
                      <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">No messages yet</p>
                      <Button 
                        variant="link" 
                        className="text-purple-600 text-sm"
                        onClick={() => setShowMessageDialog(true)}
                      >
                        Send a message to your care team
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {conversations.slice(0, 5).map((conv, index) => (
                        <div
                          key={conv.id || index}
                          className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => handleOpenConversation(conv)}
                          data-testid={`conversation-${index}`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-800 text-sm truncate">{conv.subject}</p>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-purple-500 text-xs">{conv.unread_count}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 truncate">{conv.last_message}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(conv.last_message_time).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Emergency */}
            <Card className="bg-red-50 border-red-200" data-testid="emergency-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-full">
                    <Phone className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">24/7 Support Line</p>
                    <p className="text-red-600 text-lg font-bold">703-578-7108</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Daily Check-in Dialog */}
      <Dialog open={showCheckinDialog} onOpenChange={setShowCheckinDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-teal-600" />
              Daily Check-in
            </DialogTitle>
            <DialogDescription>
              How are you feeling today? This helps your care team support you better.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Pain Level */}
            <div>
              <Label className="flex items-center justify-between mb-3">
                <span>Pain Level</span>
                <Badge className={getPainColor(checkinData.pain_level)}>
                  {checkinData.pain_level}/10
                </Badge>
              </Label>
              <Slider
                value={[checkinData.pain_level]}
                onValueChange={([v]) => setCheckinData({...checkinData, pain_level: v})}
                max={10}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>None</span>
                <span>Moderate</span>
                <span>Severe</span>
              </div>
            </div>

            {/* Mood */}
            <div>
              <Label className="mb-3 block">How&apos;s your mood?</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "great", label: "Great", emoji: "😊" },
                  { value: "good", label: "Good", emoji: "🙂" },
                  { value: "okay", label: "Okay", emoji: "😐" },
                  { value: "sad", label: "Sad", emoji: "😢" },
                  { value: "anxious", label: "Anxious", emoji: "😰" },
                  { value: "depressed", label: "Low", emoji: "😔" }
                ].map(mood => (
                  <Button
                    key={mood.value}
                    variant={checkinData.mood === mood.value ? "default" : "outline"}
                    className={checkinData.mood === mood.value ? "bg-teal-600" : ""}
                    onClick={() => setCheckinData({...checkinData, mood: mood.value})}
                  >
                    <span className="mr-1">{mood.emoji}</span>
                    {mood.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <Label className="mb-3 block">Any symptoms today?</Label>
              <div className="flex flex-wrap gap-2">
                {["Nausea", "Fatigue", "Breathing", "Confusion", "Fever", "None"].map(symptom => (
                  <Badge
                    key={symptom}
                    variant={checkinData.symptoms.includes(symptom.toLowerCase()) ? "default" : "outline"}
                    className={`cursor-pointer ${checkinData.symptoms.includes(symptom.toLowerCase()) ? "bg-teal-600" : ""}`}
                    onClick={() => {
                      const s = symptom.toLowerCase();
                      setCheckinData({
                        ...checkinData,
                        symptoms: checkinData.symptoms.includes(s)
                          ? checkinData.symptoms.filter(x => x !== s)
                          : [...checkinData.symptoms, s]
                      });
                    }}
                  >
                    {symptom}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="mb-2 block">Anything else to share?</Label>
              <Textarea
                value={checkinData.notes}
                onChange={(e) => setCheckinData({...checkinData, notes: e.target.value})}
                placeholder="Optional notes for your care team..."
                className="h-20"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckinDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitCheckin}
              disabled={isSubmittingCheckin}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {isSubmittingCheckin ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Check-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Caregiver Wellness Dialog */}
      <Dialog open={showWellnessDialog} onOpenChange={setShowWellnessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-500" />
              Caregiver Wellness Check
            </DialogTitle>
            <DialogDescription>
              Taking care of yourself is important too. Let us know how you&apos;re doing.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Stress Level */}
            <div>
              <Label className="flex items-center justify-between mb-3">
                <span>Stress Level</span>
                <Badge className={wellnessData.stress_level >= 7 ? "bg-red-500" : wellnessData.stress_level >= 5 ? "bg-yellow-500" : "bg-green-500"}>
                  {wellnessData.stress_level}/10
                </Badge>
              </Label>
              <Slider
                value={[wellnessData.stress_level]}
                onValueChange={([v]) => setWellnessData({...wellnessData, stress_level: v})}
                max={10}
                step={1}
              />
            </div>

            {/* Sleep Hours */}
            <div>
              <Label className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-2">
                  <BedDouble className="w-4 h-4" />
                  Hours of Sleep Last Night
                </span>
                <span className="font-semibold">{wellnessData.sleep_hours} hrs</span>
              </Label>
              <Slider
                value={[wellnessData.sleep_hours]}
                onValueChange={([v]) => setWellnessData({...wellnessData, sleep_hours: v})}
                max={12}
                step={0.5}
              />
            </div>

            {/* Had Respite */}
            <div className="flex items-center justify-between">
              <Label>Had any break/respite this week?</Label>
              <Switch
                checked={wellnessData.had_respite}
                onCheckedChange={(v) => setWellnessData({...wellnessData, had_respite: v})}
              />
            </div>

            {/* Needs Support */}
            <div className="flex items-center justify-between">
              <Label>Would you like support resources?</Label>
              <Switch
                checked={wellnessData.needs_support}
                onCheckedChange={(v) => setWellnessData({...wellnessData, needs_support: v})}
              />
            </div>

            {/* Notes */}
            <div>
              <Label className="mb-2 block">How can we support you?</Label>
              <Textarea
                value={wellnessData.notes}
                onChange={(e) => setWellnessData({...wellnessData, notes: e.target.value})}
                placeholder="Any concerns or needs..."
                className="h-20"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWellnessDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitWellness}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <Heart className="w-4 h-4 mr-2" />
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              Message Your Care Team
            </DialogTitle>
            <DialogDescription>
              Send a secure message to your care team. They typically respond within 24 hours.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Subject</Label>
              <Input
                type="text"
                value={newMessageSubject}
                onChange={(e) => setNewMessageSubject(e.target.value)}
                placeholder="What is this about?"
              />
            </div>
            <div>
              <Label className="mb-2 block">Message</Label>
              <Textarea
                value={newMessageText}
                onChange={(e) => setNewMessageText(e.target.value)}
                placeholder="Type your message here..."
                className="h-32"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendNewMessage}
              disabled={isSendingMessage}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSendingMessage ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversation View Dialog */}
      <Dialog open={!!selectedConversation} onOpenChange={() => setSelectedConversation(null)}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              {selectedConversation?.subject}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-3">
              {conversationMessages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`p-3 rounded-lg ${
                    msg.sender_role === "caregiver" 
                      ? "bg-purple-50 ml-8" 
                      : "bg-slate-100 mr-8"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-slate-700">
                      {msg.sender_name}
                    </span>
                    <span className="text-xs text-slate-400">
                      {new Date(msg.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-slate-800 text-sm">{msg.content}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <div className="flex gap-2 mt-4">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="flex-1 h-20"
            />
            <Button 
              onClick={handleSendReply}
              disabled={isSendingMessage || !replyText.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSendingMessage ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Care Assistant */}
      <AICareAssistant 
        isOpen={showAIAssistant} 
        onClose={() => setShowAIAssistant(false)} 
      />
    </div>
  );
}
